import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'

// ── Types ──────────────────────────────────────────────────────────────────────

export interface CustomIndicator {
  id: string
  name: string
  description: string
  code: string
  enabled: boolean
  color: string
  createdAt: number
  updatedAt: number
}

interface CustomIndicatorStore {
  indicators: CustomIndicator[]
  addIndicator: (ind: Omit<CustomIndicator, 'id' | 'createdAt' | 'updatedAt'>) => string
  updateIndicator: (id: string, patch: Partial<Omit<CustomIndicator, 'id' | 'createdAt'>>) => void
  removeIndicator: (id: string) => void
  duplicateIndicator: (id: string) => void
  toggleEnabled: (id: string) => void
  importIndicators: (indicators: CustomIndicator[]) => void
}

// ── Default code template ─────────────────────────────────────────────────────

export const DEFAULT_INDICATOR_CODE = `// Custom Indicator
// Available arrays: open, high, low, close, volume, timestamp
// Built-ins: sma(values, period), ema(values, period), rsi(values, period),
//            atr(high, low, close, period), highest(values, period),
//            lowest(values, period), macd(values, fast, slow, signal)
// Output: plot(values, options?), plotLine(values, options?), plotHistogram(values, options?)

const fastEma = ema(close, 9);
const slowEma = ema(close, 21);

plot(fastEma, { color: '#2962ff', lineWidth: 2, label: 'EMA 9' });
plot(slowEma, { color: '#ef5350', lineWidth: 2, label: 'EMA 21' });
`

// ── Store ─────────────────────────────────────────────────────────────────────

export const useCustomIndicatorStore = create<CustomIndicatorStore>()(
  persist(
    immer((set) => ({
      indicators: [],

      addIndicator: (ind) => {
        const id = crypto.randomUUID()
        set((s) => {
          s.indicators.push({
            ...ind,
            id,
            createdAt: Date.now(),
            updatedAt: Date.now(),
          })
        })
        return id
      },

      updateIndicator: (id, patch) =>
        set((s) => {
          const ind = s.indicators.find((x) => x.id === id)
          if (ind) Object.assign(ind, patch, { updatedAt: Date.now() })
        }),

      removeIndicator: (id) =>
        set((s) => {
          s.indicators = s.indicators.filter((x) => x.id !== id)
        }),

      duplicateIndicator: (id) =>
        set((s) => {
          const orig = s.indicators.find((x) => x.id === id)
          if (!orig) return
          s.indicators.push({
            ...orig,
            id: crypto.randomUUID(),
            name: `${orig.name} (copy)`,
            enabled: false,
            createdAt: Date.now(),
            updatedAt: Date.now(),
          })
        }),

      toggleEnabled: (id) =>
        set((s) => {
          const ind = s.indicators.find((x) => x.id === id)
          if (ind) ind.enabled = !ind.enabled
        }),

      importIndicators: (indicators) =>
        set((s) => {
          for (const ind of indicators) {
            const exists = s.indicators.find((x) => x.id === ind.id)
            if (exists) {
              Object.assign(exists, ind)
            } else {
              s.indicators.push(ind)
            }
          }
        }),
    })),
    { name: 'mhp-custom-indicators' }
  )
)
